/**
 * Client-side mirror of the Cognito user pool password policy defined in
 * simple-user-infra/modules/cognito/main.tf (password_policy block).
 *
 * These two MUST stay in sync. Validating anything weaker here does not make
 * signup more permissive — it just moves the rejection to the SignUp call,
 * where Cognito answers with InvalidPasswordException and no account is
 * created. That is how users ended up on the "enter your verification code"
 * screen waiting for an email nobody had asked Cognito to send.
 */

export const PASSWORD_MIN_LENGTH = 8;

// The symbol set Cognito accepts, per the AWS docs for password policies.
export const COGNITO_SYMBOLS = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+= -]/;

/**
 * Returns a human-readable description of what the password is still missing,
 * or null when it satisfies the pool policy.
 */
export const describePasswordPolicyGaps = (password: string): string | null => {
  const missing: string[] = [];

  if (password.length < PASSWORD_MIN_LENGTH) missing.push(`${PASSWORD_MIN_LENGTH} characters`);
  if (!/[A-Z]/.test(password)) missing.push('an uppercase letter');
  if (!/[a-z]/.test(password)) missing.push('a lowercase letter');
  if (!/[0-9]/.test(password)) missing.push('a number');
  if (!COGNITO_SYMBOLS.test(password)) missing.push('a special character');

  return missing.length ? `Password needs ${missing.join(', ')}.` : null;
};
